import { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsArrowCounterclockwise, BsArrowRepeat, BsCheck, BsPencil, BsSend } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import { useError } from '#globalContexts/ErrorContext';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { sanitizeHtml } from '#lib/sanitize';

import SendTestEmailModal from '../../../common/SendTestEmailModal/SendTestEmailModal';

const EmailPreviewPane = ({
	email,
	onApprove,
	onUnapprove,
	onUpdate,
	onRegenerate,
	isRegenerating,
	isUnapproving,
	campaignStatus,
	signatureContent,
	campaignId,
	emailAccountId,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editedSubject, setEditedSubject] = useState('');
	const [editedContent, setEditedContent] = useState('');
	const [showTestModal, setShowTestModal] = useState(false);
	const { showError } = useError();
	const { hasCapability: canSend } = useCapabilityCheck('email.send');

	useEffect(() => {
		if (email) {
			setEditedSubject(email.generatedSubject);
			setEditedContent(email.generatedContent);
			setIsEditing(false);
		}
	}, [email]);

	if (!email) {
		return (
			<div className="bg-white border rounded-card overflow-hidden flex flex-col py-3">
				<div className="flex-1 flex items-center justify-center text-bgBase-800">
					<p>Wählen Sie einen Empfänger aus der Liste</p>
				</div>
			</div>
		);
	}

	// Company recipients have no contact person: show "Firmenkontakt" + the company email.
	const isCompanyRecipient = !email.crmContacts;
	const recipientName = isCompanyRecipient ? 'Firmenkontakt' : email.crmContacts?.fullName;
	const recipientEmail = isCompanyRecipient ? email.recipientCompany?.emailAddress : email.crmContacts?.emailAddress;

	const handleSaveEdit = async () => {
		try {
			await onUpdate(email.id, editedSubject, editedContent);
			setIsEditing(false);
		} catch (error) {
			showError({
				title: 'Fehler',
				message: 'Die E-Mail konnte nicht gespeichert werden.',
			});
		}
	};

	const handleCancelEdit = () => {
		setIsEditing(false);
		setEditedSubject(email.generatedSubject);
		setEditedContent(email.generatedContent);
	};

	const handleRegenerate = async () => {
		try {
			await onRegenerate(email.id);
		} catch (error) {
			showError({
				title: 'Fehler',
				message: 'Die E-Mail konnte nicht neu generiert werden.',
			});
		}
	};

	return (
		<div className="bg-white border rounded-card overflow-hidden flex flex-col relative">
			{/* Loading Overlay */}
			{isRegenerating && (
				<div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
					<div className="flex flex-col items-center gap-3">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
						<p className="text-sm text-text">E-Mail wird neu generiert...</p>
					</div>
				</div>
			)}

			<div className="p-4 border-b bg-bgBase-300">
				<div className="flex items-center justify-between mb-2">
					<h3 className="font-medium text-text">E-Mail Vorschau</h3>
					<div className="flex items-center gap-2">
						{!isEditing && email.status !== 'approved' && (
							<>
								<button
									onClick={() => setIsEditing(true)}
									className="p-2 text-text hover:text-primary-600 transition-colors"
									title="Bearbeiten"
									disabled={isRegenerating}
								>
									<BsPencil className="h-5 w-5" />
								</button>
								<button
									onClick={handleRegenerate}
									className={`p-2 transition-colors ${isRegenerating ? 'text-bgBase-800 cursor-not-allowed' : 'text-text hover:text-caution-700'}`}
									title="Neu generieren"
									disabled={isRegenerating}
								>
									<BsArrowRepeat className={`h-5 w-5 ${isRegenerating ? 'animate-spin' : ''}`} />
								</button>
							</>
						)}
						{!isEditing && emailAccountId && canSend && (
							<button
								onClick={() => setShowTestModal(true)}
								className="p-2 text-text hover:text-primary-600 transition-colors"
								title="Test-E-Mail senden"
								disabled={isRegenerating}
							>
								<BsSend className="h-5 w-5" />
							</button>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2 text-sm text-text">
					<span>An:</span>
					<span className="font-medium">{recipientName}</span>
					<span className="text-bgBase-800">•</span>
					<span>{recipientEmail || 'Keine E-Mail'}</span>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto p-6">
				{isEditing ? (
					<div className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-text mb-2">Betreff</label>
							<TextInput value={editedSubject} onChange={(e) => setEditedSubject(e.target.value)} />
						</div>
						<div>
							<label className="block text-sm font-medium text-text mb-2">Inhalt</label>
							<Textarea
								value={editedContent}
								onChange={(e) => setEditedContent(e.target.value)}
								rows={15}
								className="font-mono"
							/>
						</div>
					</div>
				) : (
					<div className="space-y-4">
						<div>
							<p className="text-xs font-medium text-bgBase-800 uppercase tracking-wide mb-1">Betreff</p>
							<p className="text-lg font-semibold text-text">{email.generatedSubject}</p>
						</div>
						<div className="pt-4 border-t">
							<p className="text-xs font-medium text-bgBase-800 uppercase tracking-wide mb-3">
								E-Mail Inhalt
							</p>
							<div className="prose prose-sm max-w-none">
								<pre className="whitespace-pre-wrap font-sans text-text leading-relaxed">
									{email.generatedContent}
								</pre>
							</div>

							{/* Signature Preview */}
							{signatureContent && (
								<div className="mt-6 pt-4 border-t border-dashed border-bgBase-600">
									<p className="text-xs font-medium text-bgBase-800 uppercase tracking-wide mb-2">
										Signatur
									</p>
									<div
										className="rich-text-content max-w-none text-text"
										dangerouslySetInnerHTML={{ __html: sanitizeHtml(signatureContent) }}
									/>
								</div>
							)}
						</div>
					</div>
				)}
			</div>

			<div className="p-4 border-t bg-bgBase-300 flex items-center justify-between">
				{isEditing ? (
					<>
						<Button onClick={handleCancelEdit} variant="outline">
							Abbrechen
						</Button>
						<Button onClick={handleSaveEdit}>Speichern</Button>
					</>
				) : (
					<>
						<div className="text-sm text-bgBase-800">
							{email.userEdited && (
								<span className="text-primary-700 font-medium">Manuell bearbeitet</span>
							)}
						</div>
						{email.status !== 'approved' && (
							<Button
								onClick={() => onApprove(email.id)}
								disabled={!canSend}
								title={canSend ? '' : 'Versand ist in Ihrem aktuellen Abo nicht verfügbar'}
								className="flex items-center gap-2 bg-success-700 hover:bg-success-700/90"
							>
								<BsCheck className="h-5 w-5" />
								Genehmigen & Weiter
							</Button>
						)}
						{email.status === 'approved' && (
							<div className="flex items-center gap-3">
								<div className="flex items-center gap-2 text-success-700 font-medium">
									<BsCheck className="h-5 w-5" />
									Genehmigt
								</div>
								<Button
									onClick={() => onUnapprove(email.id)}
									variant="outline"
									disabled={isUnapproving}
									className="flex items-center gap-2"
									title={
										campaignStatus === 'active'
											? 'Die E-Mail ist bereits eingeplant und wird beim Zurückziehen storniert.'
											: undefined
									}
								>
									<BsArrowCounterclockwise className="h-5 w-5" />
									{isUnapproving ? 'Wird zurückgezogen...' : 'Zurückziehen'}
								</Button>
							</div>
						)}
					</>
				)}
			</div>

			{/* Test Email Modal */}
			{emailAccountId && (
				<SendTestEmailModal
					isOpen={showTestModal}
					onClose={() => setShowTestModal(false)}
					emailAccountId={emailAccountId}
					campaignId={campaignId}
					generatedEmailId={email.id}
				/>
			)}
		</div>
	);
};

EmailPreviewPane.propTypes = {
	email: PropTypes.shape({
		id: PropTypes.string.isRequired,
		status: PropTypes.string.isRequired,
		generatedSubject: PropTypes.string.isRequired,
		generatedContent: PropTypes.string.isRequired,
		userEdited: PropTypes.bool,
		crmContacts: PropTypes.object,
		recipientCompany: PropTypes.object,
	}),
	onApprove: PropTypes.func.isRequired,
	onUnapprove: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onRegenerate: PropTypes.func.isRequired,
	isRegenerating: PropTypes.bool,
	isUnapproving: PropTypes.bool,
	campaignStatus: PropTypes.string,
	signatureContent: PropTypes.string,
	campaignId: PropTypes.string,
	emailAccountId: PropTypes.string,
};

export default EmailPreviewPane;
